import { SITE } from "../config/site";
import { getToken } from "./api";

export type FeedbackKind = "question" | "content" | "bug" | "idea" | "other";

export interface FeedbackInput {
  kind: FeedbackKind;
  message: string;
  questionId?: string;
  email?: string;
  page?: string;
  lang: "vi" | "en";
}

export const MAX_MESSAGE = 4000;

const KIND_LABEL: Record<FeedbackKind, string> = {
  question: "Question issue",
  content: "Content accuracy",
  bug: "Bug",
  idea: "Feature idea",
  other: "Other",
};

export function validateFeedback(f: FeedbackInput): string | null {
  const msg = f.message.trim();
  if (msg.length < 10) return "too-short";
  if (msg.length > MAX_MESSAGE) return "too-long";
  if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return "bad-email";
  return null;
}

export function feedbackTitle(f: FeedbackInput): string {
  const head = f.questionId ? `[${KIND_LABEL[f.kind]}] ${f.questionId}` : `[${KIND_LABEL[f.kind]}]`;
  const first = f.message.trim().split("\n")[0]!.slice(0, 70);
  return `${head}: ${first}`;
}

export function feedbackBody(f: FeedbackInput): string {
  const lines = [
    f.message.trim(),
    "",
    "---",
    `- Type: ${KIND_LABEL[f.kind]}`,
    ...(f.questionId ? [`- Question ID: \`${f.questionId}\``] : []),
    ...(f.page ? [`- Page: ${f.page}`] : []),
    `- UI language: ${f.lang}`,
  ];
  return lines.join("\n");
}

/** URL tạo GitHub issue điền sẵn — không cần backend. Email KHÔNG đưa vào issue công khai. */
export function githubIssueUrl(f: FeedbackInput): string {
  const params = new URLSearchParams({
    title: feedbackTitle(f),
    body: feedbackBody(f),
    labels: f.kind === "question" || f.kind === "content" ? "content" : f.kind === "bug" ? "bug" : "feedback",
  });
  return `${SITE.repo}/issues/new?${params.toString()}`;
}

export async function sendFeedback(f: FeedbackInput, endpoint: string = SITE.feedbackEndpoint): Promise<void> {
  if (!endpoint) throw new Error("no-endpoint");
  const headers: Record<string, string> = { "Content-Type": "application/json", Accept: "application/json" };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(endpoint, {
    method: "POST",
    headers,
    body: JSON.stringify({
      subject: feedbackTitle(f),
      kind: f.kind,
      message: f.message.trim(),
      questionId: f.questionId ?? "",
      email: f.email?.trim() ?? "",
      page: f.page ?? "",
      lang: f.lang,
    }),
  });
  if (!res.ok) throw new Error(`http-${res.status}`);
}
